/*
  Assessment Item Loader
  - Loads Stage 1 (core domains) and Stage 2 (cross-domain) assessment banks
  - Provides helpers for indexing items and retrieving scoring config
  - Future live assessment can call these APIs to fetch items by element/domain
*/

// Stage 1 assets
import stage1Items from "../data/assessment/items.json";
import stage1Scoring from "../data/assessment/scoring.json";

// Stage 2 assets (optional)
// Keep in a separate folder to avoid mixing pilots
import stage2Items from "../data/assessment-stage2/items.json";
import stage2Scoring from "../data/assessment-stage2/scoring.json";

/**
 * Load assessment items for a given stage.
 * @param {1|2} stage - 1 for core domains, 2 for cross-domain
 * @returns {Array<object>} Array of item objects
 */
export function loadAssessmentItems(stage = 1) {
	if (stage === 2) return stage2Items;
	return stage1Items;
}

/**
 * Load scoring configuration for a given stage.
 * @param {1|2} stage
 * @returns {Record<string, { itemIds: string[], reverseItemIds: string[], formatWeights: Record<string, number> }>} scoring config
 */
export function loadScoring(stage = 1) {
	if (stage === 2) return stage2Scoring;
	return stage1Scoring;
}

/**
 * Build indexes to speed up lookups by element or domain.
 * @param {Array<object>} items - item array from loadAssessmentItems
 * @returns {{ byElement: Record<string, object[]>, byDomain: Record<string, object[]>, forcedPairs: Array<{a: string, b: string}> }}
 */
export function buildAssessmentIndex(items) {
	const byElement = {};
	const byDomain = {};
	const forcedPairs = [];

	for (const item of items) {
		const { elementId, domainId } = item;
		if (elementId) {
			byElement[elementId] = byElement[elementId] || [];
			byElement[elementId].push(item);
		}
		if (domainId) {
			byDomain[domainId] = byDomain[domainId] || [];
			byDomain[domainId].push(item);
		}
		if (item.format === "forced" && item.pairWithElementId) {
			forcedPairs.push({ a: item.elementId, b: item.pairWithElementId });
		}
	}

	return { byElement, byDomain, forcedPairs };
}

/**
 * Example usage (wire this into a provider/hook in the live assessment):
 *
 * const items = loadAssessmentItems(1);
 * const scoring = loadScoring(1);
 * const index = buildAssessmentIndex(items);
 *
 * // render engine can now pull items by domain/element and track formats
 */
